import { useState, useEffect } from "react"
import Button from "components/ui/Button"

const ACTION_OPTIONS = [
  { value: "before", label: "Before" },
  { value: "after", label: "After" },
  { value: "exact", label: "Exact" },
  { value: "immediate", label: "Immediate" },
]

const TRIGGER_OPTIONS = [
  { value: "closingDate", label: "Closing Date" },
  { value: "auctionDate", label: "Auction Date" },
  { value: "firstOpenHome", label: "First Open Home" },
  { value: "anyOpenHome", label: "Any Open Home" },
  { value: "campaignCreated", label: "Campaign Created" },
  { value: "sold", label: "Sold" },
  { value: "underContract", label: "Under Contract" },
  { value: "auctionPassedIn", label: "Auction Passed In" },
  { value: "closingViaPT", label: "Closing Via PT" },
  { value: "priceGuideUpdated", label: "Price Guide Updated" },
  { value: "goLive", label: "Go Live" },
]

const SALE_PROCESS_OPTIONS = [
  { value: "privateTreaty", label: "Private Treaty" },
  { value: "auction", label: "Auction" },
]

const SCENARIO_OPTIONS = [
  { value: "any", label: "Any" },
  { value: "upfront", label: "Upfront" },
  { value: "afterOpenHomes", label: "After Open Homes" },
  { value: "noClosingDate", label: "No Closing Date" },
]

const WEEKDAY_OPTIONS = [
  { value: "", label: "None" },
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
]

const PLATFORM_OPTIONS = [
  { value: "app", label: "App" },
  { value: "sms", label: "SMS" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "email", label: "Email" },
]

const BuyerCampaignTemplateForm = ({ template, onSubmit, onCancel, formMode }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    action: "before",
    trigger: "closingDate",
    offsetDays: 0,
    weekOnMarket: "",
    weekday: "",
    time: "09:00",
    saleProcess: ["privateTreaty"],
    scenario: "any",
    minInspections: 0,
    content: "",
    prompt: "",
    isPostDeadline: false,
    platform: ["app", "sms"],
    isActive: true,
    specialInstructions: "",
    objections: [],
  })

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || "",
        description: template.description || "",
        action: template.action || "before",
        trigger: template.trigger || "closingDate",
        offsetDays: template.offsetDays ?? 0,
        weekOnMarket: template.weekOnMarket ?? "",
        weekday: template.weekday || "",
        time: template.time || "09:00",
        saleProcess: template.saleProcess || ["privateTreaty"],
        scenario: template.scenario || "any",
        minInspections: template.minInspections ?? 0,
        content: template.content || "",
        prompt: template.prompt || "",
        isPostDeadline: template.isPostDeadline ?? false,
        platform: template.platform || ["app", "sms"],
        isActive: template.isActive ?? true,
        specialInstructions: template.specialInstructions || "",
        objections: template.objections || [],
      })
    }
  }, [template])

  const handleObjectionChange = (index, field, value) => {
    const current = [...formData.objections]
    current[index] = { ...current[index], [field]: value }
    setFormData({ ...formData, objections: current })
  }

  const handleAddObjection = () => {
    setFormData({
      ...formData,
      objections: [
        ...formData.objections,
        { objection: "", preempt: "", howItLands: "" },
      ],
    })
  }

  const handleRemoveObjection = (index) => {
    const current = [...formData.objections]
    current.splice(index, 1)
    setFormData({ ...formData, objections: current })
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    })
  }

  const handleNumberChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: parseInt(value, 10) || 0,
    })
  }

  const handleSaleProcessChange = (value) => {
    const current = [...formData.saleProcess]
    const idx = current.indexOf(value)
    if (idx > -1) {
      if (current.length > 1) {
        current.splice(idx, 1)
      }
    } else {
      current.push(value)
    }
    setFormData({ ...formData, saleProcess: current })
  }

  const handlePlatformChange = (value) => {
    const current = [...formData.platform]
    const idx = current.indexOf(value)
    if (idx > -1) {
      current.splice(idx, 1)
    } else {
      current.push(value)
    }
    setFormData({ ...formData, platform: current })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const submitData = { ...formData }
    if (!submitData.weekday) {
      submitData.weekday = null
    }
    if (!submitData.weekOnMarket) {
      submitData.weekOnMarket = null
    }
    onSubmit(submitData)
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-6">
        {/* Name */}
        <div>
          <label htmlFor="name" className="form-label">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="form-input border border-mediumgray"
            placeholder="e.g. Comparable Sales"
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="form-label">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            rows={2}
            value={formData.description}
            onChange={handleChange}
            className="form-input border"
            placeholder="Brief description of this template"
          />
        </div>

        {/* Scheduling: Action & Trigger */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="action" className="form-label">
              Action <span className="text-red-500">*</span>
            </label>
            <select
              id="action"
              name="action"
              value={formData.action}
              onChange={handleChange}
              required
              className="form-select border border-mediumgray"
            >
              {ACTION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="trigger" className="form-label">
              Trigger <span className="text-red-500">*</span>
            </label>
            <select
              id="trigger"
              name="trigger"
              value={formData.trigger}
              onChange={handleChange}
              required
              className="form-select border border-mediumgray"
            >
              {TRIGGER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Offset Days, Weekday, Time */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="offsetDays" className="form-label">
              Offset Days
            </label>
            <input
              type="number"
              id="offsetDays"
              name="offsetDays"
              value={formData.offsetDays}
              onChange={handleNumberChange}
              min={0}
              className="form-input border border-mediumgray"
            />
            <p className="text-xs text-darkgray mt-1">
              Days before/after trigger
            </p>
          </div>

          <div>
            <label htmlFor="weekOnMarket" className="form-label">
              Week on market
            </label>
            <input
              type="number"
              id="weekOnMarket"
              name="weekOnMarket"
              value={formData.weekOnMarket}
              onChange={handleNumberChange}
              min={1}
              className="form-input border border-mediumgray"
            />
            <p className="text-xs text-darkgray mt-1">
              With the Go Live trigger — which week since go live to send in
            </p>
          </div>

          <div>
            <label htmlFor="weekday" className="form-label">
              Weekday
            </label>
            <select
              id="weekday"
              name="weekday"
              value={formData.weekday}
              onChange={handleChange}
              className="form-select border border-mediumgray"
            >
              {WEEKDAY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-darkgray mt-1">
              Snap to specific weekday instead of offset
            </p>
          </div>

          <div>
            <label htmlFor="time" className="form-label">
              Time (Sydney)
            </label>
            <input
              type="time"
              id="time"
              name="time"
              value={formData.time}
              onChange={handleChange}
              className="form-input border border-mediumgray"
            />
            <p className="text-xs text-darkgray mt-1">
              Leave empty for exact trigger time
            </p>
          </div>
        </div>

        {/* Sale Process */}
        <div>
          <label className="form-label">
            Sale Process <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-wrap gap-3 mt-1">
            {SALE_PROCESS_OPTIONS.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.saleProcess.includes(opt.value)}
                  onChange={() => handleSaleProcessChange(opt.value)}
                  className="h-4 w-4 text-darkgray border-mediumgray rounded"
                />
                <span className="text-sm">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Scenario */}
        <div>
          <label htmlFor="scenario" className="form-label">
            Scenario
          </label>
          <select
            id="scenario"
            name="scenario"
            value={formData.scenario}
            onChange={handleChange}
            className="form-select border border-mediumgray"
          >
            {SCENARIO_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Minimum inspections */}
        <div>
          <label htmlFor="minInspections" className="form-label">
            Minimum inspections
          </label>
          <input
            type="number"
            id="minInspections"
            name="minInspections"
            value={formData.minInspections}
            onChange={handleNumberChange}
            min={0}
            className="form-input border border-mediumgray"
          />
          <p className="text-xs text-darkgray mt-1">
            Distinct buyers who inspected, below which this message is not scheduled
          </p>
        </div>

        {/* Content */}
        <div>
          <label htmlFor="content" className="form-label">
            Content
          </label>
          <textarea
            id="content"
            name="content"
            rows={5}
            value={formData.content}
            onChange={handleChange}
            className="form-input border"
            placeholder="Message content with [PLACEHOLDER] variables..."
          />
        </div>

        {/* Prompt */}
        <div>
          <label htmlFor="prompt" className="form-label">
            AI Prompt
          </label>
          <textarea
            id="prompt"
            name="prompt"
            rows={3}
            value={formData.prompt}
            onChange={handleChange}
            className="form-input border"
            placeholder="Instructions for AI personalisation..."
          />
        </div>

        {/* Platform */}
        <div>
          <label className="form-label">Platform</label>
          <div className="flex flex-wrap gap-3 mt-1">
            {PLATFORM_OPTIONS.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.platform.includes(opt.value)}
                  onChange={() => handlePlatformChange(opt.value)}
                  className="h-4 w-4 text-darkgray border-mediumgray rounded"
                />
                <span className="text-sm">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Toggles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isPostDeadline"
              name="isPostDeadline"
              checked={formData.isPostDeadline}
              onChange={handleChange}
              className="h-4 w-4 text-darkgray border-mediumgray rounded"
            />
            <label htmlFor="isPostDeadline" className="text-sm font-medium text-darkergray">
              Post Deadline
            </label>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
              className="h-4 w-4 text-darkgray border-mediumgray rounded"
            />
            <label htmlFor="isActive" className="text-sm font-medium text-darkergray">
              Active
            </label>
          </div>
        </div>

        {/* Objections */}
        <div>
          <label className="form-label">Objections</label>
          <div className="space-y-3">
            {formData.objections.length === 0 && (
              <p className="text-sm text-darkgray">No objections added.</p>
            )}
            {formData.objections.map((obj, idx) => (
              <div key={idx} className="p-3 border rounded bg-lightergray">
                <div>
                  <label className="form-label">Objection</label>
                  <input
                    type="text"
                    value={obj.objection || ""}
                    onChange={(e) => handleObjectionChange(idx, "objection", e.target.value)}
                    className="form-input border"
                    placeholder="Short objection text"
                  />
                </div>

                <div className="mt-2">
                  <label className="form-label">Preempt</label>
                  <textarea
                    rows={2}
                    value={obj.preempt || ""}
                    onChange={(e) => handleObjectionChange(idx, "preempt", e.target.value)}
                    className="form-input border"
                    placeholder="How to preempt this objection"
                  />
                </div>

                <div className="mt-2">
                  <label className="form-label">How It Lands</label>
                  <textarea
                    rows={2}
                    value={obj.howItLands || ""}
                    onChange={(e) => handleObjectionChange(idx, "howItLands", e.target.value)}
                    className="form-input border"
                    placeholder="How this message should land"
                  />
                </div>

                <div className="flex justify-end mt-2">
                  <Button type="button" className="outline-button" onClick={() => handleRemoveObjection(idx)}>
                    Remove
                  </Button>
                </div>
              </div>
            ))}

            <div>
              <Button type="button" onClick={handleAddObjection} className="outline-button">
                Add Objection
              </Button>
            </div>
          </div>
        </div>

        {/* Special Instructions */}
        <div>
          <label htmlFor="specialInstructions" className="form-label">
            Special Instructions
          </label>
          <textarea
            id="specialInstructions"
            name="specialInstructions"
            rows={2}
            value={formData.specialInstructions}
            onChange={handleChange}
            className="form-input border"
            placeholder="Any special instructions..."
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4">
          <Button type="button" onClick={onCancel} className="outline-button">
            Cancel
          </Button>
          <Button type="submit" className="black-button">
            {formMode === "create" ? "Create Template" : "Update Template"}
          </Button>
        </div>
      </div>
    </form>
  )
}

export default BuyerCampaignTemplateForm
